/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  DeviceMobile, EnvelopeSimple, ShieldCheck, FloppyDisk, Info,
  ArrowsLeftRight, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

export default function AdminOTPSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/otp-settings");
      setS(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load OTP settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/otp-settings", patch);
      setS(r.data);
      toast.success("OTP settings updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  // Mutual-exclusion guard: switching `dual` on forces mobile+email on.
  // Turning either off while `dual` is on, also turns `dual` off.
  const toggle = async (key) => {
    if (!s) return;
    const next = { ...s, [key]: !s[key] };
    if (key === "dual_enabled" && next.dual_enabled) {
      next.mobile_enabled = true;
      next.email_enabled = true;
    }
    if ((key === "mobile_enabled" || key === "email_enabled") && !next[key]) {
      next.dual_enabled = false;
    }
    await save({
      mobile_enabled: next.mobile_enabled,
      email_enabled: next.email_enabled,
      dual_enabled: next.dual_enabled,
    });
  };

  if (!s) {
    return <AppShell title="OTP Settings" subtitle="Administration · System Settings">
      <div className="p-6 text-sm text-[#8A8780]">Loading…</div>
    </AppShell>;
  }

  const allOff = !(s.mobile_enabled || s.email_enabled || s.dual_enabled);

  return (
    <AppShell title="OTP Settings" subtitle="Administration · System Settings · Customer authentication">
      <div className="max-w-3xl">
        {/* Intro card */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-5 mb-6" data-testid="otp-settings-intro">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" color="#2D5A27"/>
            <h2 className="font-heading text-lg">OTP Verification Options</h2>
          </div>
          <p className="text-sm text-[#5C5A55] mt-2 leading-relaxed">
            Control how customers receive a one-time code during signup and login.
            At least one channel must remain enabled — disabling all of them silently
            re-enables mobile.
          </p>
        </div>

        {/* Toggles */}
        <div className="space-y-3">
          <ToggleRow
            Icon={DeviceMobile}
            color="#2D5A27"
            title="Mobile OTP Verification"
            blurb="Send a 6-digit code to the customer's registered mobile number. Uses the existing OTP gateway (SMS provider integration coming soon)."
            badge="Default"
            on={s.mobile_enabled}
            disabled={busy || s.dual_enabled}
            onChange={() => toggle("mobile_enabled")}
            testid="toggle-mobile"
          />
          <ToggleRow
            Icon={EnvelopeSimple}
            color="#0B2B66"
            title="Email OTP Verification"
            blurb="Send a 6-digit code to the customer's email address. Customers must provide their email during signup before requesting the OTP."
            on={s.email_enabled}
            disabled={busy || s.dual_enabled}
            onChange={() => toggle("email_enabled")}
            testid="toggle-email"
          />
          <ToggleRow
            Icon={ArrowsLeftRight}
            color="#C25D38"
            title="Dual OTP Verification"
            blurb="Generate a single OTP and dispatch it to both mobile and email. Customers can enter the code from either channel — the system validates against the same OTP."
            on={s.dual_enabled}
            disabled={busy}
            onChange={() => toggle("dual_enabled")}
            testid="toggle-dual"
            note={s.dual_enabled ? "Both Mobile & Email OTP are auto-enabled in this mode." : ""}
          />
        </div>

        {/* Status banner */}
        <div className="mt-6 rounded-md border p-4 flex items-start gap-3"
              style={{
                background: allOff ? "#FDECE6" : "#F4F8F2",
                borderColor: allOff ? "#F0DAD3" : "#D4E0D1",
              }}>
          {allOff
            ? <WarningCircle size={18} weight="duotone" color="#C25D38"/>
            : <CheckCircle size={18} weight="duotone" color="#2D5A27"/>}
          <div className="text-sm">
            <div className="font-medium text-[#1A1A1A]">
              {allOff
                ? "Mobile OTP will be force-enabled as a safety fallback."
                : `Active channels: ${activeLabel(s)}`}
            </div>
            {s.updated_at && (
              <div className="text-xs text-[#8A8780] mt-0.5">
                Last updated {new Date(s.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Future-API hint */}
        <div className="mt-6 bg-white border border-dashed border-[#E5E2D9] rounded-md p-4 flex items-start gap-3 text-xs text-[#5C5A55]" data-testid="future-api-note">
          <Info size={14} color="#8A8780"/>
          <div>
            <div className="font-medium text-[#1A1A1A] mb-0.5">Future integration settings</div>
            SMS gateway picker (MSG91, Twilio, AWS SNS, Gupshup) and per-template
            sender-ID configuration will appear here in the next iteration.
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function ToggleRow({ Icon, color, title, blurb, badge, on, disabled, onChange, testid, note }) {
  return (
    <div className={`bg-white border rounded-md p-5 flex items-start gap-4 ${disabled ? "opacity-70" : ""}`}
          style={{ borderColor: on ? color : "#E5E2D9", borderLeftWidth: on ? 4 : 1 }}
          data-testid={testid}>
      <div className="w-10 h-10 rounded-md flex-none flex items-center justify-center"
            style={{ background: `${color}1A`, color }}>
        <Icon size={18} weight="duotone"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-heading text-base">{title}</div>
          {badge && (
            <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded"
                   style={{ background: "#F0ECE3", color: "#5C5A55" }}>{badge}</span>
          )}
          <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded ml-auto`}
                  style={{ background: on ? `${color}1A` : "#F7F6F2", color: on ? color : "#8A8780" }}>
            {on ? "ON" : "OFF"}
          </span>
        </div>
        <p className="text-xs text-[#5C5A55] mt-1.5 leading-relaxed">{blurb}</p>
        {note && <p className="text-[11px] text-[#8A8780] mt-1.5 italic">{note}</p>}
      </div>
      <button onClick={onChange} disabled={disabled} data-testid={`${testid}-switch`}
               aria-pressed={on}
               className={`relative inline-flex h-7 w-12 rounded-full transition-colors flex-none mt-1
                 ${on ? "" : "bg-[#E5E2D9]"} disabled:cursor-not-allowed`}
               style={{ background: on ? color : undefined }}>
        <span className={`absolute top-0.5 inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`}/>
      </button>
    </div>
  );
}

function activeLabel(s) {
  if (s.dual_enabled) return "Mobile + Email (dual)";
  const labels = [];
  if (s.mobile_enabled) labels.push("Mobile");
  if (s.email_enabled) labels.push("Email");
  return labels.join(" + ") || "—";
}
